import React, { useEffect, useMemo } from 'react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useStockDashboardStore } from '../../../store/stockDashboardStore';
import { useWidgetSize } from './useWidgetSize';

interface RecentActivityWidgetProps {
  title: string;
  accentColor: string;
  bgTint: string;
  isDark: boolean;
}

const fmtDate = (d: Date) =>
  d.toLocaleString('en-ZA', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

const RecentActivityWidget: React.FC<RecentActivityWidgetProps> = ({ title, accentColor: _ac, bgTint, isDark }) => {
  const { ref, width, isXS, isSM } = useWidgetSize();
  const { recentLogs, stockUnits, items, fetchRecentLogs } = useInventoryStore();
  const { globalScopeWarehouseId } = useStockDashboardStore();

  useEffect(() => {
    fetchRecentLogs();
  }, [fetchRecentLogs]);

  const scopedUnitIds = useMemo(() => {
    const units = globalScopeWarehouseId
      ? stockUnits.filter(u => u.warehouseId === globalScopeWarehouseId)
      : stockUnits;
    return new Set(units.map(u => u.id));
  }, [stockUnits, globalScopeWarehouseId]);

  const scopedLogs = useMemo(() =>
    recentLogs.filter(l => scopedUnitIds.has(l.stockUnitId)),
    [recentLogs, scopedUnitIds]
  );

  const unitById = useMemo(() => new Map(stockUnits.map(u => [u.id, u])), [stockUnits]);
  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const defaultBg = isDark ? '#20242b' : '#ffffff';
  const bg        = bgTint || defaultBg;
  const headerBg  = (bgTint === 'transparent' || !bgTint) ? defaultBg : bgTint;

  const showDate = width >= 260;
  const showRef  = width >= 380;

  const padC   = isXS ? 'p-2'   : 'p-3';
  const headC  = isXS ? 'text-xs' : isSM ? 'text-sm' : 'text-base';
  const tableC = isXS ? 'text-[10px]' : 'text-xs';
  const pyC    = isXS ? 'py-1' : 'py-1.5';
  const badgeMinW = isXS ? 'min-w-[70px]' : 'min-w-[86px]';

  const EVENT_LABEL: Record<string, string> = {
    Added:   'Added',
    Edited:  'Edited',
    add_stock:    'Added',
    return_stock: 'Returned',
    transfer:     'Transferred',
    check_out:    'Checked Out',
  };

  const actionBadgeStyle = (eventType: string, movementType: string | null, dark: boolean) => {
    const key = movementType ?? eventType;
    switch (key) {
      case 'check_out':
        return {
          backgroundColor: dark ? 'rgba(217,119,6,0.2)' : '#fef3c7',
          color: dark ? '#fbbf24' : '#b45309',
        };
      case 'transfer':
        return {
          backgroundColor: dark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
          color: dark ? '#60a5fa' : '#1d4ed8',
        };
      case 'return_stock':
        return {
          backgroundColor: dark ? 'rgba(20,184,166,0.2)' : '#ccfbf1',
          color: dark ? '#2dd4bf' : '#0f766e',
        };
      case 'Edited':
        return {
          backgroundColor: dark ? 'rgba(107,114,128,0.25)' : '#f3f4f6',
          color: dark ? '#9ca3af' : '#4b5563',
        };
      case 'Added':
      case 'add_stock':
      default:
        return {
          backgroundColor: dark ? 'rgba(22,163,74,0.2)' : '#dcfce7',
          color: dark ? '#4ade80' : '#15803d',
        };
    }
  };

  return (
    <div
      ref={ref}
      className={`h-full flex flex-col ${padC} rounded border overflow-hidden`}
      style={{ backgroundColor: bg, borderColor: isDark ? '#343a43' : '#e5e7eb' }}
    >
      <h3 className={`${headC} font-semibold mb-2 flex-shrink-0 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        {title}
      </h3>
      {scopedLogs.length === 0 ? (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No activity yet.</p>
      ) : (
        <div className="overflow-auto flex-1">
          <table className="w-full">
            <thead className="sticky top-0" style={{ backgroundColor: headerBg }}>
              <tr className={`text-left border-b ${tableC} ${isDark ? 'text-gray-400 border-[#3a404a]' : 'text-gray-500 border-gray-200'}`}>
                {showDate && <th className={`${pyC} pr-3 font-medium whitespace-nowrap`}>DATE</th>}
                <th className={`${pyC} pr-3 font-medium`}>ACTION</th>
                <th className={`${pyC} pr-3 font-medium`}>ITEM</th>
                <th className={`${pyC} pr-3 font-medium`}>USER</th>
                {showRef && <th className={`${pyC} font-medium`}>REF</th>}
              </tr>
            </thead>
            <tbody className={`${tableC} ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              {scopedLogs.map((log) => {
                const unit  = unitById.get(log.stockUnitId);
                const item  = unit ? itemById.get(unit.itemId) : null;
                const label = EVENT_LABEL[log.movementType ?? log.eventType] ?? log.eventType;
                const badgeStyle = actionBadgeStyle(log.eventType, log.movementType, isDark);
                return (
                  <tr key={log.id} className={`border-b ${isDark ? 'border-[#343a43]' : 'border-gray-100'}`}>
                    {showDate && (
                      <td className={`${pyC} pr-3 whitespace-nowrap`}>{fmtDate(log.createdAt)}</td>
                    )}
                    <td className={`${pyC} pr-3`}>
                      <span
                        className={`inline-flex ${badgeMinW} justify-center px-2 py-0.5 rounded-full`}
                        style={{
                          fontSize: isXS ? 10 : 11,
                          ...badgeStyle,
                        }}
                      >
                        {label}
                      </span>
                    </td>
                    <td className={`${pyC} pr-3`}>{item?.name ?? '—'}</td>
                    <td className={`${pyC} pr-3 whitespace-nowrap`} style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                      {log.actedBy ?? '—'}
                    </td>
                    {showRef && <td className={pyC}>{log.reference ?? '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecentActivityWidget;
